import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Avatar, Username, VipBadge } from "./UserBadge";
import { 
  User, LogOut, Crown, Shield, Settings, MessageSquare, 
  ChevronDown, Wallet
} from "lucide-react";

export default function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (!user) return null;

  const points = user.points || 0;

  return (
    <div ref={ref} className="relative" data-testid="user-menu">
      {/* ─── Trigger Button in Header ─── */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-2.5 py-1 rounded-lg border transition-all duration-200 hover:scale-[1.02] ${
          open 
            ? "bg-white/10 border-white/30 text-white shadow-md" 
            : "bg-[#0d1017] hover:bg-white/5 border-white/10 hover:border-white/20 text-zinc-200"
        }`}
        data-testid="user-menu-toggle"
      >
        <Avatar user={user} size={28} hover={false} />
        
        <div className="hidden lg:flex flex-col text-left leading-tight min-w-0">
          <Username user={user} className="text-xs font-bold" asText />
          <span className="text-[10px] text-zinc-400 font-mono">
            {points.toLocaleString()} pts
          </span>
        </div>

        <ChevronDown className={`hidden lg:block w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${open ? "rotate-180 text-white" : ""}`} />
      </button>

      {/* ─── Solid Opaque Dropdown Menu (No Transparency) ─── */}
      {open && (
        <div 
          className="absolute right-0 mt-2 w-64 bg-[#0d1017] border border-white/20 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.99)] z-[9999] overflow-hidden"
          data-testid="user-menu-dropdown"
        >
          {/* User Profile Header Card */}
          <div className="p-3.5 border-b border-white/10 bg-[#121620]">
            <div className="flex items-center gap-3">
              <Avatar user={user} size={40} hover={false} />
              <div className="flex-1 min-w-0">
                <Username user={user} asText className="text-sm font-bold truncate block text-white" />
                <div className="text-[11px] text-zinc-400 font-mono truncate">{user.email}</div>
              </div>
            </div>

            {/* Badges Row */}
            <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
              {((user.loadout?.banner_badge && user.loadout.banner_badge !== "none") || (user.vip_tier && user.vip_tier !== "none")) && (
                <VipBadge tier={user.loadout?.banner_badge || user.vip_tier} />
              )}
              {user.role === "root" && (
                <span className="userBanner userBanner--yellow notranslate" translate="no">
                  <span className="userBanner-before"></span>
                  <strong>ROOT</strong>
                  <span className="userBanner-after"></span>
                </span>
              )}
              {user.role === "admin" && user.role !== "root" && (
                <span className="userBanner userBanner--red notranslate" translate="no">
                  <span className="userBanner-before"></span>
                  <strong>ADMIN</strong>
                  <span className="userBanner-after"></span>
                </span>
              )}
              {user.role === "mod" && (
                <span className="userBanner userBanner--royalBlue notranslate" translate="no">
                  <span className="userBanner-before"></span>
                  <strong>MODERATOR</strong>
                  <span className="userBanner-after"></span>
                </span>
              )}
              {user.group_name && user.role !== "root" && user.role !== "admin" && (
                <span 
                  className="px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest rounded bg-white/10 text-zinc-300 border border-white/15 notranslate"
                  style={{ fontFamily: 'Rajdhani', fontWeight: 800 }}
                  translate="no"
                >
                  {user.group_name}
                </span>
              )}
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="p-1.5 space-y-0.5 bg-[#0d1017]">
            <Item icon={User} label="Profilul meu" to={`/u/${user.username}`} onClose={() => setOpen(false)} testid="usermenu-profile" />
            <Item icon={Crown} label="Loadout VIP & Magazin" to="/loadout" onClose={() => setOpen(false)} testid="usermenu-loadout" accent />
            <Item icon={Wallet} label="Portofel & Plăți" href="/donate?tab=recharge" target="_blank" onClose={() => setOpen(false)} testid="usermenu-wallet" gold />
            <Item icon={MessageSquare} label="Mesaje Private" to="/messages" onClose={() => setOpen(false)} testid="usermenu-messages" />
            <Item icon={Settings} label="Setări profil" to={`/u/${user.username}?edit=1`} onClose={() => setOpen(false)} testid="usermenu-edit" />
            
            {(user.role === "admin" || user.role === "root") && (
              <Item icon={Shield} label="Admin Control Panel" to="/admin" onClose={() => setOpen(false)} testid="usermenu-admin" admin />
            )}
          </nav>

          {/* Logout Section */}
          <div className="border-t border-white/10 p-1.5 bg-[#121620]">
            <button
              onClick={() => { setOpen(false); logout(); navigate("/"); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg transition-all"
              data-testid="usermenu-logout"
            >
              <LogOut className="w-4 h-4" /> 
              <span>Deconectare</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Item({ icon: Icon, label, to, href, target, onClose, testid, accent, admin, gold }) {
  const content = (
    <>
      <Icon className={`w-4 h-4 shrink-0 ${
        admin ? "text-amber-400" : 
        gold ? "text-amber-400" :
        accent ? "text-amber-400" : 
        "text-zinc-400 group-hover:text-white"
      }`} />
      <span className="flex-1 text-left">{label}</span>
    </>
  );

  const classes = `w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all group ${
    admin
      ? "text-amber-400 hover:bg-amber-500/15 hover:text-amber-300 font-bold"
      : gold
      ? "text-amber-400 hover:bg-amber-500/15 hover:text-amber-300 font-semibold"
      : accent
      ? "text-amber-400 hover:bg-amber-500/15 hover:text-white font-semibold"
      : "text-zinc-300 hover:bg-white/[0.08] hover:text-white"
  }`;

  if (href) {
    return (
      <a href={href} target={target} rel="noreferrer" onClick={onClose} className={classes} data-testid={testid}>
        {content}
      </a>
    );
  }

  return (
    <Link to={to} onClick={onClose} className={classes} data-testid={testid}>
      {content}
    </Link>
  );
}
